
import { translateServiceInstance } from 'dp-formio';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';

const ar = 'ar';
/**
 * Model of history :
 * map(indicatorId,map(domainId,map(questionId,taskId)))
 *
 * Empty Map :
 * {"dataType":"Map","value":[]}
 *
 *  Demo of Data :
 * {"dataType":"Map","value":[["indicator1",{"dataType":"Map","value":[["domain1",{"dataType":"Map","value":[["question1","taskId"],["question2","taskId"],["question3","taskId"]]}],["domain2",{"dataType":"Map","value":[["question4","taskId"],["question5","taskId"]]}]]}],["indicator2",{"dataType":"Map","value":[["domain3",{"dataType":"Map","value":[["question6","taskId"]]}]]}]]
 *
 */

export class ClassificationIndicator {
    _id: string;
    requestId: string;

    key: string;
    order: number;
    code: string;
    icon: string;

    //config
    domains: ClassificationDomain[] = [];
    attachmentsFormKey: string;
    domainNotesAndRecommendationsFormKey: string;
    isCached: boolean;

    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    tooltipAr: string;
    tooltipEn: string;
    get name() {
        return translateServiceInstance?.currentLang == ar ? this.nameAr : this.nameEn;
    }
    get description() {
        return translateServiceInstance?.currentLang == ar ? this.descriptionAr : this.descriptionEn;
    }
    get tooltip() {
        return translateServiceInstance?.currentLang == ar ? this.tooltipAr : this.tooltipEn;
    }

    //score
    averageToPass: number;
    errorAr: string;
    errorEn: string;
    get error() {
        return translateServiceInstance?.currentLang == ar ? this.errorAr : this.errorEn;
    }
    get hasError() {
        if ((this.score ?? 0) < this.averageToPass) return true;
        else return false;
    }
    get isValid() {
        return !this.hasError && this.isSubDomainsValids;
    }
    get calculatedScore() {
        let score = 0;
        let domainsCount = 0;
        // calc from questions
        this.domains.filter(q => q.score).forEach(q => {
            score += +q.score * (q.weight || 1);
            domainsCount += q.weight || 1;
        });

        if (!domainsCount) return 0;
        return score / domainsCount;
    }

    //result
    score: number;
    status: ClassificationStatus;
    isSubDomainsValids: boolean;
    history = new Map();
    get hasComments() {
        return this.history.has(this._id);
    }

    completionRate: number = 0;
}

export class ClassificationDomain {
    _id: string;
    requestId: string;

    key: string;
    order: number;
    code: string;
    icon: string;
    indicator: string;

    //config
    indicatorObject: { _id: string, history: Map<any, any> };
    questions: ClassificationQuestion[] = [];
    isCached: boolean;

    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    tooltipAr: string;
    tooltipEn: string;
    get name() {
        return translateServiceInstance?.currentLang == ar ? this.nameAr : this.nameEn;
    }
    get description() {
        return translateServiceInstance?.currentLang == ar ? this.descriptionAr : this.descriptionEn;
    }
    get tooltip() {
        return translateServiceInstance?.currentLang == ar ? this.tooltipAr : this.tooltipEn;
    }

    //attachment
    attachmentNameAr: string;
    attachmentNameEn: string;
    attachmentsTooltipAr: string;
    attachmentsTooltipEn: string;
    attachmentIsRequired: boolean;
    get attachmentName() {
        return translateServiceInstance?.currentLang == ar ? this.attachmentNameAr : this.attachmentNameEn;
    }
    get attachmentsTooltip() {
        return translateServiceInstance?.currentLang == ar ? this.attachmentsTooltipAr : this.attachmentsTooltipEn;
    }
    get hasAttachments() { return !!this.attachmentNameAr || !!this.attachmentNameEn }

    //score
    weight: number;
    averageToPass: number;
    errorAr: string;
    errorEn: string;
    get error() {
        return translateServiceInstance?.currentLang == ar ? this.errorAr : this.errorEn;
    }
    get hasError() {
        if ((this.score ?? 0) < this.averageToPass || this.questions.some(q => !q.isValid)) return true;
        else return false;
    }
    get calculatedScore() {
        let score = 0;
        let questionsCount = 0;
        // calc from questions
        this.questions.filter(q => q.isValid && !q.notApply && q.type != 'years').forEach(q => {
            score += (+q.score || 0) * (q.weight || 1);
            questionsCount += q.weight || 1;
        });

        if (!questionsCount) return 0;
        return score / questionsCount;
    }

    //result
    score: number;
    decision: ClassificationStatus;
    notesAndRecommendations: any[] = [];
    attachmentsFiles: ClassificationAttachments[] = [];
    get attachmentsValid() {
        if (!this.attachmentIsRequired) return true;
        return !!this.attachmentsFiles?.length;
    }

    get isValid() {
        if (!this.attachmentsValid || this.questions.some(q => q.hasChange || !q.isSaved)) return false;
        return !this.hasError;
    }
    get hasComments() {
        if (this.indicatorObject.history.has(this.indicatorObject._id))
            return this.indicatorObject.history.get(this.indicatorObject._id).has(this._id);
        return false;
    }

    completionRate: number = 0;
}

export class ClassificationQuestion {
    _id: string;
    requestId: string;

    key: string;
    order: number;
    code: string;
    icon: string;
    domain: string;
    domainObject: ClassificationDomain;

    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    tooltipAr: string;
    tooltipEn: string;
    get name() {
        return translateServiceInstance?.currentLang == ar ? this.nameAr : this.nameEn;
    }
    get description() {
        return translateServiceInstance?.currentLang == ar ? this.descriptionAr : this.descriptionEn;
    }
    get tooltip() {
        return translateServiceInstance?.currentLang == ar ? this.tooltipAr : this.tooltipEn;
    }
    type: string;
    min: number;
    max: number;
    years: ClassificationYears[];

    //attachment
    attachmentNameAr: string;
    attachmentNameEn: string;
    attachmentsTooltipAr: string;
    attachmentsTooltipEn: string;
    attachmentIsRequired: boolean;
    get attachmentName() {
        return translateServiceInstance?.currentLang == ar ? this.attachmentNameAr : this.attachmentNameEn;
    }
    get attachmentsTooltip() {
        return translateServiceInstance?.currentLang == ar ? this.attachmentsTooltipAr : this.attachmentsTooltipEn;
    }
    get hasAttachments() { return !!this.attachmentNameAr || !!this.attachmentNameEn }

    //score
    weight: number;
    averageToPass: number;
    errorAr: string;
    errorEn: string;
    get error() {
        return translateServiceInstance?.currentLang == ar ? this.errorAr : this.errorEn;
    }
    get hasError() {
        if (this.notApply) return false;
        return (+this.score || 0) * (+this.weight || 1) < this.averageToPass;
    }

    //result
    score: number = null;
    status: ClassificationStatus = ClassificationStatus.UnComplete;
    notApply: boolean = false;
    isSaved: boolean = false;
    notes: string = null;
    attachmentsFiles: ClassificationAttachments[] = [];
    get attachmentsValid() {
        if (this.hasAttachments && this.attachmentIsRequired)
            return !!this.attachmentsFiles?.length;
        return true;
    }

    resetResult() {
        this.score = this.min || null;
        this.notes = null;
        this.attachmentsFiles = [];
        this.years.forEach(q => q.value = '0');
    }

    get isValid() {
        if (this.notApply && !this.notes) return false;
        else if (this.notApply && this.notes) return true;
        else if (!this.attachmentsValid) return false;
        else if (this.type == 'years') {
            if (this.years.some(q => !q.value)) return false;
        }
        else if ((this.min && this.min > this.score) || (this.max && this.max < this.score)) return false;
        return !this.hasError;
    }

    orginal: ClassificationQuestion;
    instanceForEdit: ClassificationQuestion;
    get hasChange() {
        if (this.instanceForEdit) {
            const changed = !_isEqual(
                _omit(this.instanceForEdit, ['orginal', 'domainObject', 'instanceForEdit']),
                _omit(this.instanceForEdit.orginal, ['orginal', 'domainObject', 'instanceForEdit'])
            );
            return changed;
        }
        const changed = !_isEqual(
            _omit(this, ['orginal', 'domainObject']),
            _omit(this.orginal, ['orginal', 'domainObject'])
        );
        return this.isValid && (changed || !this.isSaved);
    }

    get hasComments() {
        if (this.domainObject?.hasComments)
            return this.domainObject.indicatorObject.history
                .get(this.domainObject.indicatorObject._id)
                .get(this.domainObject._id)
                .has(this._id);
        return '';
    }
}

export enum ClassificationStatus {
    UnComplete, Complete
}
export class ClassificationAttachments {
    fileName: string;
    file: string;
}

export class ClassificationYears {
    key: string;
    nameAr: string;
    nameEn: string;
    get name() {
        return translateServiceInstance?.currentLang == ar ? this.nameAr : this.nameEn;
    }

    value: string;
}

export class ManagersNotes {
    _id: string;
    requestId: string;
    questionSubmissionId: string;
    taskId: string;
    userName: string;
    givenName: string;
    email: string;
    role: string;
    roleByTask: string;
    message: string;
    created: string;

    fromMe = false;

    get name() {
        if (this.givenName?.includes('@'))
            return this.givenName.substring(0, this.givenName.indexOf('@'))
        return this.givenName;
    }
}

export class CompletionRate {
    constructor(
        public domainCompletionRate: number,
        public indicatorCompletionRate: number
    ) { }
}